import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config';
import {
  clearStoredAccessToken,
  getStoredAccessToken,
  isPublicEndpoint,
  isTokenExpired,
  isUnauthenticatedAuthRoute,
  refreshAccessToken,
  shouldProactivelyRefreshToken,
} from './authTokenRefresh';

// ============================================================================
// Request Deduplication (ISSUE-066 fix)
// ============================================================================

/**
 * RequestDeduplicator class prevents duplicate concurrent API requests.
 * When multiple identical GET requests are made simultaneously, only one
 * network request is sent and all callers receive the same response.
 */
class RequestDeduplicator {
  private pendingRequests: Map<string, Promise<any>> = new Map();

  /**
   * Generate a unique key for a request based on method, URL, and params
   */
  private generateKey(method: string, url: string, params?: any): string {
    const paramStr = params ? JSON.stringify(params) : '';
    return `${method.toUpperCase()}:${url}:${paramStr}`;
  }

  /**
   * Deduplicate a request - if an identical request is pending, return its promise
   * Otherwise, execute the request and track it
   */
  async deduplicate<T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // Check if there's already a pending request with this key
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    // Execute the request and track it
    const promise = requestFn().finally(() => {
      // Remove from pending requests when complete (success or error)
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Wrap an API GET request with deduplication
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const key = this.generateKey('GET', url, config?.params);
    return this.deduplicate(key, () => apiClient.get(url, config).then(res => res.data));
  }

  /**
   * Clear all pending requests (useful for cleanup)
   */
  clear(): void {
    this.pendingRequests.clear();
  }

  /**
   * Get the number of pending requests
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }
}

// Export singleton instance for use across services
export const requestDeduplicator = new RequestDeduplicator();

// ============================================================================
// Response Cache with TTL (ISSUE-066 fix)
// ============================================================================

/**
 * ResponseCache provides time-based caching for API responses.
 * Reduces redundant API calls by returning cached data within TTL window.
 */
class ResponseCache {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private defaultTTL: number;

  constructor(defaultTTL: number = 5000) {
    this.defaultTTL = defaultTTL;
  }

  /**
   * Get cached data if it exists and hasn't expired
   */
  get(key: string, ttl?: number): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const effectiveTTL = ttl ?? this.defaultTTL;
    if (Date.now() - cached.timestamp > effectiveTTL) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  /**
   * Store data in cache
   */
  set(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Remove a specific key from cache
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.cache.size;
  }
}

// Export singleton instance for use across services
export const responseCache = new ResponseCache(5000); // 5 second default TTL

// ============================================================================
// Axios Client Configuration
// ============================================================================

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 360000, // 360 seconds (6 minutes) to match backend timeout for long-running LLM operations
  headers: {
    'Content-Type': 'application/json',
  },
});

type RetryableAxiosRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

// Add request interceptor for authentication and token validation
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url || '';
    const token = getStoredAccessToken();

    if (isUnauthenticatedAuthRoute(url)) {
      return config;
    }

    if (!token) {
      return config;
    }

    if (isPublicEndpoint(url)) {
      if (!isTokenExpired(token)) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    // Expired → refresh; if still unusable after failure, clear. Last ≤25% of TTL with
    // usage → proactive refresh; on failure keep the still-valid access token.
    const expired = isTokenExpired(token);
    const proactive = !expired && shouldProactivelyRefreshToken(token);
    if (expired || proactive) {
      const refreshed = await refreshAccessToken();
      const renewed = getStoredAccessToken();
      if (renewed && !isTokenExpired(renewed)) {
        config.headers.Authorization = `Bearer ${renewed}`;
        return config;
      }
      if (expired) {
        clearStoredAccessToken();
        return config;
      }
      // Proactive refresh failed; continue with still-valid access token.
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    config.headers.Authorization = `Bearer ${token}`;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Suppress CORS errors (expected when backend is not configured for CORS)
    const isCorsError =
      error.code === 'ERR_NETWORK' ||
      error.message?.includes('CORS') ||
      error.message?.includes('Access-Control-Allow-Origin') ||
      (!error.response && error.request);

    if (isCorsError) {
      // Silently suppress CORS errors - these are expected backend configuration issues
      // Return a rejected promise but don't log
      return Promise.reject(error);
    }

    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const url = originalRequest.url || '';
      if (!isUnauthenticatedAuthRoute(url)) {
        originalRequest._retry = true;
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          const token = getStoredAccessToken();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          }
        }
        clearStoredAccessToken();
      }
    }

    // Handle 403 Forbidden - often happens when token is expired or missing
    if (error.response?.status === 403) {
      const token = getStoredAccessToken();
      // If no token exists, 403 is expected for auth-required endpoints - suppress logging
      if (!token || isTokenExpired(token)) {
        // Silently handle - token is expired or missing, this is expected
        return Promise.reject(error);
      }
      // Token exists and is valid, but got 403 - might be permissions issue
      // Only log in development to avoid console spam
      if (process.env.NODE_ENV === 'development') {
        console.warn('[API Client] 403 Forbidden (may be permissions issue):', error.config?.url);
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const token = getStoredAccessToken();
      const errorDetail = error.response?.data?.detail || error.response?.data?.message || 'Unauthorized';

      // Log detailed error information
      if (process.env.NODE_ENV === 'development') {
        console.warn('[API Client] 401 Unauthorized:', {
          url: error.config?.url,
          detail: errorDetail,
          tokenExists: !!token,
          tokenExpired: token ? isTokenExpired(token) : null,
        });
      }
    }
    return Promise.reject(error);
  }
);
